/**
 * Auth store (Zustand) — cookie-based auth с мультитенантностью и OAuth.
 * Токены хранятся в httpOnly cookies, управляются сервером.
 */
import { create } from 'zustand';
import api from '../services/api';

interface TenantInfo {
  id: number;
  name: string;
  slug: string;
  domain?: string;
  site_url?: string;
  plan: string;
  status: string;
}

interface AvailableTenant {
  id: number;
  name: string;
  slug: string;
  plan: string;
  status: string;
  role: string;
  is_current: boolean;
  logo_url?: string;
}

interface AdminInfo {
  id: number;
  email: string;
  name: string;
  role: string;
}

interface AuthState {
  admin: AdminInfo | null;
  tenant: TenantInfo | null;
  availableTenants: AvailableTenant[];
  isAuthenticated: boolean;
  isLoading: boolean;
  isSwitching: boolean;
  // Авторизован, но нет привязки к тенанту — нужен онбординг
  needsOnboarding: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  fetchMe: () => Promise<void>;
  switchTenant: (tenantId: number) => Promise<void>;
  // OAuth авторизация — обработка ответа от VK/Telegram
  oauthLogin: (data: { available_tenants?: AvailableTenant[] }) => Promise<void>;
}

const loadFromStorage = <T,>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const useAuthStore = create<AuthState>((set, get) => ({
  admin: loadFromStorage<AdminInfo>('admin_info'),
  tenant: loadFromStorage<TenantInfo>('tenant_info'),
  availableTenants: loadFromStorage<AvailableTenant[]>('available_tenants') || [],
  isAuthenticated: false,
  isLoading: false,
  isSwitching: false,
  needsOnboarding: false,

  login: async (email: string, password: string) => {
    set({ isLoading: true });
    try {
      const { data } = await api.post('/auth/admin/login', { email, password });
      // Токены установлены сервером в httpOnly cookies

      // Сохраняем available_tenants из ответа логина (не-секретные данные)
      if (data.available_tenants) {
        localStorage.setItem('available_tenants', JSON.stringify(data.available_tenants));
        set({ availableTenants: data.available_tenants });
      }

      set({ isAuthenticated: true, isLoading: false });
      await get().fetchMe();
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Даже при ошибке сервера — сбрасываем состояние
    }
    localStorage.removeItem('admin_info');
    localStorage.removeItem('tenant_info');
    localStorage.removeItem('available_tenants');
    set({ admin: null, tenant: null, availableTenants: [], isAuthenticated: false, needsOnboarding: false });
  },

  checkAuth: async () => {
    try {
      await get().fetchMe();
      // needsOnboarding устанавливается в fetchMe
      set({ isAuthenticated: true });
    } catch {
      set({ isAuthenticated: false, needsOnboarding: false, admin: null, tenant: null, availableTenants: [] });
    }
  },

  fetchMe: async () => {
    try {
      const { data } = await api.get('/auth/admin/me');
      const adminInfo: AdminInfo = data.admin;
      const tenantInfo: TenantInfo = data.tenant;
      const availableTenants: AvailableTenant[] = data.available_tenants || [];

      localStorage.setItem('admin_info', JSON.stringify(adminInfo));
      localStorage.setItem('tenant_info', JSON.stringify(tenantInfo));
      localStorage.setItem('available_tenants', JSON.stringify(availableTenants));

      // Если tenant=null — пользователь не привязан к тенанту, нужен онбординг
      set({ admin: adminInfo, tenant: tenantInfo, availableTenants, needsOnboarding: !tenantInfo });
    } catch (error) {
      console.error('Failed to fetch admin profile:', error);
      throw error;
    }
  },

  switchTenant: async (tenantId: number) => {
    set({ isSwitching: true });
    try {
      await api.post('/auth/admin/switch-tenant', { tenant_id: tenantId });
      // Новые токены установлены сервером в httpOnly cookies

      // Перезагрузить профиль с данными нового тенанта
      await get().fetchMe();

      // Перезагрузить страницу для сброса кэшей компонентов
      window.location.href = '/admin/';
    } catch (error) {
      console.error('Failed to switch tenant:', error);
      set({ isSwitching: false });
      throw error;
    }
  },

  // OAuth авторизация — сохранение не-секретных данных и загрузка профиля
  oauthLogin: async (data) => {
    // Токены установлены сервером в httpOnly cookies
    if (data.available_tenants) {
      localStorage.setItem('available_tenants', JSON.stringify(data.available_tenants));
      set({ availableTenants: data.available_tenants });
    }
    set({ isAuthenticated: true });
    await get().fetchMe();
  },
}));
